import { MoreHoriz } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

// ── The name of the structure under the pointer ──────────────────────
//
// The label is pinned where the pick resolved, not chased under the
// cursor: an interactive portal sitting under the pointer swallows the
// canvas mouse events the viewport needs, and a label that moves cannot
// be reached.
//
// The button is the whole answer to "without popping up unbidden": the
// menu opens on a CLICK of it, never on hover-dwell. Hovering the label
// highlights the button and does nothing else.
//
// The button exists only while a pick is shown, so the keyboard reaches
// it only after the pointer has raised the label; once focused, it opens
// the menu from the keyboard too. That is acceptable here only because it
// is redundant: the same deletions are on the keyboard shortcut and, once
// open, the menu itself is fully keyboard-operable.

/** Offset from the pick, so the label never covers what it names. */
const OFFSET_PX = { x: 12, y: 12 };

export interface HoveredStructureLabelProps {
    /**
     * Receives the label's own element, so the frame can tell whether the
     * pointer is over it. Reaching for the button takes the pointer OFF
     * the contour that raised the label, and without this the pick would
     * drop and the label vanish from under the hand moving toward it.
     */
    containerRef?: React.Ref<HTMLDivElement>;
    name: string;
    /** Client coordinates the pick resolved at. */
    clientX: number;
    clientY: number;
    /** Bounding rect of the frame the label is positioned inside. */
    frameRect: { left: number; top: number } | undefined;
    /**
     * Whether to offer the actions. False leaves the plain name: the pick
     * names ANY visible structure, and narrowing that to the selection
     * would trade identification for discoverability.
     */
    offerActions: boolean;
    onOpenMenu: (clientX: number, clientY: number) => void;
}

export default function HoveredStructureLabel({
    containerRef,
    name,
    clientX,
    clientY,
    frameRect,
    offerActions,
    onOpenMenu,
}: HoveredStructureLabelProps) {
    const intl = useIntl();
    if (!frameRect) {
        return null;
    }
    return (
        <Box
            ref={containerRef}
            // The position is inline, not in `sx`: it changes per pick, and
            // emotion would mint a fresh class for every pixel it lands on.
            style={{
                left: clientX - frameRect.left + OFFSET_PX.x,
                top: clientY - frameRect.top + OFFSET_PX.y,
            }}
            sx={{
                position: 'absolute',
                zIndex: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'rgba(0, 0, 0, 0.72)',
                color: 'common.white',
                // The text must not eat canvas events; only the button
                // takes them back.
                pointerEvents: 'none',
                maxWidth: 260,
            }}
            data-testid="hovered-structure-label"
        >
            <Typography
                variant="caption"
                noWrap
                sx={{ pointerEvents: 'none' }}
                data-testid="hovered-structure-name"
            >
                {name}
            </Typography>
            {offerActions && (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'contourDeletion.labelActions',
                    })}
                    placement="top"
                >
                    <IconButton
                        size="small"
                        sx={{
                            pointerEvents: 'auto',
                            p: 0.125,
                            color: 'inherit',
                        }}
                        onClick={(event) => {
                            // The canvas below must not also see this as a
                            // click that selects or draws.
                            event.stopPropagation();
                            // A click raised from the keyboard (detail 0)
                            // carries no pointer position, so the menu
                            // opens on the button itself.
                            if (event.detail === 0) {
                                const rect =
                                    event.currentTarget.getBoundingClientRect();
                                onOpenMenu(
                                    rect.left + rect.width / 2,
                                    rect.top + rect.height / 2
                                );
                                return;
                            }
                            onOpenMenu(event.clientX, event.clientY);
                        }}
                        data-testid="hovered-structure-actions"
                    >
                        <MoreHoriz fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}
